import { useState, useEffect } from "react";
import { History } from "lucide-react";
import { formatDate } from "../lib/formatDate";
import { Card, CardHeader, CardTitle, CardContent } from "./ui/card";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationPrevious,
  PaginationLink,
  PaginationNext,
} from "./ui/pagination";
import {
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
  Table,
} from "./ui/table";
import {
  useGetTopupHistoryQuery,
  useGetSupportedMintsQuery,
} from "../generated/graphql";
// import { useNavigate } from "react-router-dom";
import { useToast } from "../components/ui/use-toast";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import { formatNumberToString } from "../lib/formatNumbers";

interface SwapProps {
  page: number;
  onPrevious: () => void;
  onNext: () => void;
  organizationId: string;
}
const TopupTableBase = ({
  page,
  onPrevious,
  onNext,
  organizationId,
}: SwapProps) => {
  // const navigate = useNavigate();
  const { toast } = useToast();

  const [{ data: mintsData, error: mintsError }] = useGetSupportedMintsQuery();
  const mints = mintsData?.getSupportedMints;

  const [{ data: topupHistoryData, error: topupHistoryError }] =
    useGetTopupHistoryQuery({
      variables: {
        organizationId: organizationId,
        page: page,
      },
    });
  const topupHistory = topupHistoryData?.getTopupHistory;

  useEffect(() => {
    if (topupHistoryError) {
      toast({
        title: "Error",
        description: topupHistoryError.message,
        variant: "destructive",
      });
    }
    if (mintsError) {
      toast({
        title: "Error",
        description: mintsError.message,
        variant: "destructive",
      });
    }
  }, [toast, topupHistoryError, mintsError]);

  console.log(topupHistory);

  //   amount
  // :
  // "0.005"
  // createdAt
  // :
  // "2024-09-16T17:15:49.106Z"
  // decision
  // :
  // "Approved"
  // decisionAt
  // :
  // "2024-10-01T18:38:14.047Z"
  // finalizedAt
  // :
  // "2024-09-16T17:18:30.518Z"
  // id
  // :
  // "90c1ffa1-c04a-4df4-ae93-308e424749cf"
  // mint
  // :
  // {id: 1, __typename: 'Mint'}
  // txHash
  // :

  return (
    <Card className="xl:col-span-2">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-lg font-semibold flex items-center">
          Top Up History
          <History className="ml-2 h-4 w-4 opacity-50" />
        </CardTitle>
      </CardHeader>
      <CardContent>
        {topupHistory?.length === 0 ? (
          <div className="text-center m-6">No top-ups found</div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>ID</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Currency</TableHead>
                  <TableHead>Decision</TableHead>
                  <TableHead>DecisionAt</TableHead>
                  <TableHead>FinalizedAt</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {topupHistory?.map((topUp, idx) => (
                  <TableRow
                    key={idx}
                    onClick={() => {
                      const explorerUrl =
                        topUp.mint.chain === "BTC"
                          ? process.env.REACT_APP_BTC_EXPLORER_URL
                          : process.env.REACT_APP_ETH_EXPLORER_URL;
                      window.open(`${explorerUrl}${topUp.txHash}`, "_blank");
                    }}
                  >
                    <TableCell>{formatDate(topUp.createdAt)}</TableCell>
                    <TableCell>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            {topUp.id?.slice(0, 6)}...
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{topUp.id}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </TableCell>
                    <TableCell>{formatNumberToString(topUp.amount)}</TableCell>
                    <TableCell
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        marginRight: "10px",
                      }}
                    >
                      {mints?.find((mint) => mint.id === topUp?.mint?.id)
                        ?.img ?? undefined ? (
                        <>
                          <img
                            src={
                              mints?.find((mint) => mint.id === topUp?.mint?.id)
                                ?.img ?? undefined
                            }
                            alt={"Currency"}
                            style={{
                              width: "24px",
                              height: "24px",
                              marginRight: "5px",
                            }}
                          />
                          {mints?.find((mint) => mint.id === topUp?.mint?.id)
                            ?.symbol ?? undefined}
                        </>
                      ) : (
                        topUp?.mint?.id
                      )}
                    </TableCell>
                    <TableCell>{topUp.decision}</TableCell>
                    <TableCell>{formatDate(topUp?.decisionAt ?? "")}</TableCell>
                    <TableCell>
                      {formatDate(topUp?.finalizedAt ?? "")}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}

        <Pagination>
          <PaginationContent>
            <PaginationItem>
              {page >= 1 ? <PaginationPrevious onClick={onPrevious} /> : null}
            </PaginationItem>
            <PaginationItem>
              <PaginationLink>{page + 1}</PaginationLink>
            </PaginationItem>
            <PaginationItem>
              <PaginationNext onClick={onNext} />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </CardContent>
    </Card>
  );
};

export const TopupHistory = ({
  organizationId,
}: {
  organizationId: string;
}) => {
  const [swapPage, setSwapPage] = useState(0);

  return (
    <TopupTableBase
      page={swapPage}
      onPrevious={() => setSwapPage(swapPage - 1)}
      onNext={() => setSwapPage(swapPage + 1)}
      organizationId={organizationId}
    />
  );
};
